// bd-deepcontext-qiwz7 — two-way sync of the scheduled-job 'Traffic from'
// (countryCodes) field with the countries carried by attached brand
// blueprints. Kept as a pure, dependency-light module so the provenance /
// union / remove logic can be unit-tested without mounting the full
// ScheduledJobs component (mirrors scheduledJobFormDefaults.ts).
//
// Behaviour:
//   - Attaching a blueprint adds its countries to the field (deduped union).
//   - Removing a blueprint removes that blueprint's countries UNLESS another
//     still-attached blueprint also contributes the same country.
//   - Countries the user added/edited by hand are NOT blueprint-owned and are
//     preserved across blueprint attach/remove.
//   - A blueprint with empty/null countries contributes nothing.
//
// To know which countries are safe to remove, the form tracks PROVENANCE: the
// set of blueprint-contributed countries currently reflected in the field.
// Anything in the field that is not blueprint-contributed is treated as manual.

/** Blueprint `countries` is a comma-separated lowercase ISO2 string (e.g.
 *  "lt,es,us"), often null/empty. Form `countryCodes` is a string[] consumed by
 *  CountryMultiSelect. Split, trim, drop blanks, and normalise to lowercase so
 *  blueprint and form values compare/dedupe consistently. */
export function parseBlueprintCountries(
  countries: string | null | undefined,
): string[] {
  if (!countries) return [];
  return countries
    .split(",")
    .map((c) => c.trim().toLowerCase())
    .filter((c) => c.length > 0);
}

/** Deduped union of every attached blueprint's countries (lowercased). The map
 *  is keyed by promptId; entries for prompts not currently attached are
 *  ignored. */
export function unionBlueprintCountries(
  attachedPromptIds: string[],
  countriesByBlueprint: Record<string, string[]>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of attachedPromptIds) {
    for (const code of countriesByBlueprint[id] ?? []) {
      const norm = code.trim().toLowerCase();
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        out.push(norm);
      }
    }
  }
  return out;
}

export interface SyncResult {
  /** The new value for the form's countryCodes field. */
  countryCodes: string[];
  /** The set of country codes (lowercased) currently owned by blueprints —
   *  i.e. the provenance to carry into the next sync. */
  blueprintOwned: string[];
}

/**
 * Recompute the 'Traffic from' field after the set of attached blueprints
 * changes.
 *
 * @param currentCountryCodes  current field value (may mix case; preserved as-is for manual entries).
 * @param prevBlueprintOwned   country codes (lowercased) the blueprints owned BEFORE this change (provenance).
 * @param attachedPromptIds    blueprint ids attached AFTER this change.
 * @param countriesByBlueprint promptId -> that blueprint's countries (any case; normalised here).
 *
 * Manual countries (present in the field but never blueprint-owned) are kept.
 * A country that was blueprint-owned but is no longer contributed by any
 * attached blueprint is dropped — unless the user had also added it manually,
 * which is indistinguishable once merged, so blueprint provenance wins for
 * removal (documented behaviour: removing a blueprint removes its uniquely
 * contributed countries).
 */
export function syncCountriesFromBlueprints(
  currentCountryCodes: string[],
  prevBlueprintOwned: string[],
  attachedPromptIds: string[],
  countriesByBlueprint: Record<string, string[]>,
): SyncResult {
  const nextOwnedList = unionBlueprintCountries(
    attachedPromptIds,
    countriesByBlueprint,
  );
  const nextOwned = new Set(nextOwnedList);
  const prevOwned = new Set(prevBlueprintOwned.map((c) => c.toLowerCase()));

  // Manual codes = current field values that were NOT blueprint-owned before.
  // Compare on a lowercased key but keep the user's original casing in output.
  const result: string[] = [];
  const inResult = new Set<string>();
  const add = (code: string) => {
    const key = code.toLowerCase();
    if (!inResult.has(key)) {
      inResult.add(key);
      result.push(code);
    }
  };

  for (const code of currentCountryCodes) {
    const key = code.toLowerCase();
    const wasBlueprintOwned = prevOwned.has(key);
    if (!wasBlueprintOwned) {
      // Manual entry — always preserve.
      add(code);
    } else if (nextOwned.has(key)) {
      // Still contributed by an attached blueprint — keep.
      add(code);
    }
    // else: was blueprint-owned, no longer contributed -> drop.
  }

  // Add any newly-contributed blueprint countries not already present.
  for (const code of nextOwnedList) {
    add(code);
  }

  return { countryCodes: result, blueprintOwned: nextOwnedList };
}
